/**
 * The player's face.
 *
 * ========================== WHY THIS SCREEN EXISTS =============================
 * The engine names the viewer "You" and that stays — `game.ts` puts a null `avatarId`
 * on every seat on purpose, so a portrait can never leak an archetype, and
 * `tests/cast.test.ts` holds it there. The hero's picture is therefore a CLIENT fact:
 * the player picks a face off the same roster the cast is drawn from, it is kept on
 * this device, and the table is handed the resolved URL through `avatarUrl`. Nothing
 * about the engine, the seat or the wire changes.
 *
 * This is also the Founder Ladder's "roster rung" (master plan, Phase F), which is why
 * it is a plain component with no gate in it: Phase F adds one — a locked entry, a
 * Legend Wall face, a custom portrait — by wrapping the grid, not by rewriting it.
 *
 * Until a face is picked the table draws the silhouette, which is the honest state:
 * "no portrait yet", not a broken image.
 * ===============================================================================
 */
import { ROSTER, avatarUrl } from "../art/roster";
import { getPersona, setPersona, usePrefsStore } from "./prefs";
import "../rooms/rooms.css";

/**
 * The face on the seat, resolved.
 *
 * One function, used by the table, the saloon header and this screen, so "which
 * portrait is mine" has one answer. Null means the silhouette.
 */
export function personaAvatar(size: 128 | 512 = 128): string | null {
  const slug = getPersona();
  return slug ? avatarUrl(slug, "neutral", size) : null;
}

/** A slug's display name — the roster is keyed by slug, and the slug reads well enough. */
export function personaName(slug: string): string {
  return slug.replace(/-(m|f)$/, "").replace(/-/g, " ").replace(/^./, (c) => c.toUpperCase());
}

export function PersonaPicker({ onDone, compact }: { onDone?: () => void; compact?: boolean }) {
  const { persona } = usePrefsStore();
  /*
   * The two dealer slugs are not offered.
   *
   * `roster.ts` reserves `dealer-f` for the blackjack room's dealer and doubles
   * `dealer-m` across two cast members. A player wearing the dealer's face at a table
   * the dealer is also at is a picture nobody can read, so the grid is the 26 characters
   * and the dealers stay behind the shoe.
   */
  const faces = ROSTER.filter((r) => !r.slug.startsWith("dealer-"));
  const seen = new Set<string>();
  const unique = faces.filter((r) => (seen.has(r.slug) ? false : (seen.add(r.slug), true)));

  return (
    <section className={"persona" + (compact ? " persona-compact" : "")}>
      <div className="persona-intro">
        <h2>Pick your face</h2>
        <p className="muted">
          The table calls you "You". This is what the other players see — and what sits
          on your seat and in the saloon. You can change it whenever you like.
        </p>
      </div>

      <ul className="persona-grid">
        {unique.map((r) => {
          const on = persona === r.slug;
          return (
            <li key={r.slug}>
              <button type="button" className={"persona-face" + (on ? " on" : "")}
                      aria-pressed={on} aria-label={personaName(r.slug)}
                      onClick={() => setPersona(r.slug)}>
                <img src={avatarUrl(r.slug, on ? "amused" : "neutral", 128)} alt="" loading="lazy" />
                <span>{personaName(r.slug)}</span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="persona-go">
        <button className="btn" type="button" disabled={!persona} onClick={onDone}>
          That's me
        </button>
        {persona && (
          <button className="btn-quiet" type="button" onClick={() => setPersona(null)}>
            Go back to the silhouette
          </button>
        )}
      </div>
    </section>
  );
}
